import { Table } from '@shared/types';
import { useWaitlist } from '../../hooks/useWaitlist';
import { User } from 'firebase/auth';
import { UserProfile } from '@shared/types';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { WaitlistModal } from '../common/WaitlistModal';

interface TableCardProps {
  table: Table;
  roomId: string;
  onTableSelect: (table: Table | null) => void;
  selected: boolean;
  user?: User | null;
  userProfile?: UserProfile | null;
}

export function TableCard({ table, roomId, onTableSelect, selected, user, userProfile }: TableCardProps) {
  const navigate = useNavigate();
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  
  const { 
    waitlist,
    waitlistCount,
    isUserOnWaitlist,
    joinWaitlist,
    leaveWaitlist,
    loading: waitlistLoading,
    error: waitlistError,
    isJoining,
    isLeaving,
    displayNames,
  } = useWaitlist(roomId, table.id, user || null, userProfile || null, { enabled: true });

  const getStatusBadge = (status: string) => {
    const baseClasses = 'badge';
    switch (status) {
      case 'Open':
        return `${baseClasses} badge-success`; // Green - Game going, immediate openings
      case 'Running':
        return `${baseClasses} badge-info`; // Blue - Game going, no open seats
      case 'Interest':
        return `${baseClasses} badge-warning`; // Yellow - Waitlist open, game not running
      case 'Closed':
        return `${baseClasses} badge-danger`; // Red - Table closed
      default:
        return `${baseClasses} badge-info`;
    }
  };

  const getWaitlistText = () => {
    if (table.status === 'Closed') return 'Closed';
    if (waitlistLoading) return 'Loading...';
    return `${waitlistCount} waiting`;
  };

  const getWaitlistColor = () => {
    if (table.status === 'Closed') return 'text-red-600';
    if (waitlistLoading) return 'text-gray-500';
    if (waitlistCount === 0) return 'text-gray-500';
    if (waitlistCount >= 3) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getTableTitle = () => {
    return table.name;
  };


  return (
    <div
      className={`card card-hover cursor-pointer transition-all duration-200 ${
        selected ? 'ring-2 ring-primary-500 bg-primary-50' : ''
      }`}
      onClick={() => onTableSelect(selected ? null : table)}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {getTableTitle()}
          </h3>
          <div className="flex items-center space-x-2 mb-2">
            <span className={getStatusBadge(table.status)}>
              {table.status}
            </span>
          </div>
        </div>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Stakes:</span>
          <span className="font-medium text-gray-900">{table.stakes}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Tables:</span>
          <span className="font-medium text-gray-900">{table.number_of_tables}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Waitlist:</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (waitlistCount > 0 && table.status !== 'Closed') {
                setIsWaitlistModalOpen(true);
              }
            }}
            className={`font-medium transition-colors ${
              waitlistCount > 0 && table.status !== 'Closed' 
                ? `${getWaitlistColor()} hover:underline cursor-pointer` 
                : getWaitlistColor()
            }`}
            disabled={waitlistCount === 0 || table.status === 'Closed'}
          >
            {getWaitlistText()}
          </button>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        {table.status === 'Closed' ? (
          <div className="text-center">
            <span className="text-sm text-gray-500">Table is closed</span>
          </div>
        ) : (
          <div className="flex space-x-2">
            {!user ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/login');
                }}
                className="btn btn-primary flex-1"
              >
                Sign In to Join Waitlist
              </button>
            ) : (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (isUserOnWaitlist) {
          leaveWaitlist();
        } else {
          joinWaitlist();
        }
      }}
                className={`btn flex-1 ${
                  isUserOnWaitlist 
                    ? 'btn-error' // Red button if user is on waitlist
                    : 'btn-primary' // Blue button to join waitlist
                }`}
                disabled={waitlistLoading || isJoining || isLeaving}
              >
                {waitlistLoading ? 'Loading...' : 
                 isJoining ? 'Joining...' : 
                 isLeaving ? 'Leaving...' : 
                 isUserOnWaitlist ? 'Leave Waitlist' : 'Join Waitlist'}
              </button>
            )}
          </div>
        )}
        {waitlistError && (
          <div className="mt-2 text-center">
            <span className="text-sm text-red-600">{waitlistError.message}</span>
          </div>
        )}
      </div>

      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={isWaitlistModalOpen}
        onClose={() => setIsWaitlistModalOpen(false)}
        waitlist={waitlist}
        tableName={getTableTitle()}
        displayNames={displayNames}
      />
    </div>
  );
}
